/**
 * GoalMountain — duplicate of KingOfTheMountain for the company-wide GOAL view.
 * Self-contained. Does NOT touch KingOfTheMountain.jsx; the dashboard's KOTM
 * is unaffected by anything that happens in this file.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────┐
 *   │  ICON  METRIC NAME            CURRENT / TARGET   │   ← top banner
 *   │        Period Goal            % to peak          │     (always anchored)
 *   ├──────────────────────────────────────────────────┤
 *   │                                                  │
 *   │            🏆  ← trophy at peak                  │
 *   │             /\                                   │
 *   │            /  \    [meter on right]              │
 *   │     ......\....\                                 │   ← mountain art
 *   │           /  ◉  → ──── meter mark                │   ← climber + connector
 *   │          /────                                   │
 *   └──────────────────────────────────────────────────┘
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { UseCompany } from "../../hooks/company";
import { GET_IMAGE } from "../../helpers/images";
import Trophy3D from "../Trophy3D/Trophy3D";

const VIEW_W = 965;
const VIEW_H = 615;

/**
 * Climbing trail — built from the EXACT same line + polyline segments KOTM
 * uses to draw the visible dashed trail (lines 920-1100 in KingOfTheMountain.jsx).
 * The previous TRAIL_PATH was the invisible motion-path #person1-line which
 * starts at a different coordinate; this one matches what the user sees on
 * the dashboard, including all the switchback zigzags up the central ridge.
 */
const TRAIL_PATH = [
  "M 413 614",
  "L 415.32 610.74",
  "L 419.69 604.57",
  "L 452.5 558.34",
  "L 454.68 555.26",
  "L 457 552",
  "L 455.48 548.3",
  "L 452.89 541.98",
  "L 443.81 519.86",
  "L 442.52 516.7",
  "L 441 513",
  "L 443.93 510.28",
  "L 448.95 505.63",
  "L 476.56 480.04",
  "L 479.07 477.72",
  "L 482 475",
  "L 478.27 473.56",
  "L 470.96 470.73",
  "L 451 463",
  "L 424.6 436.6",
  "L 421.83 433.83",
  "L 419 431",
  "L 420.77 427.41",
  "L 423.93 420.99",
  "L 447.65 372.8",
  "L 449.23 369.59",
  "L 451 366",
  "L 447.85 363.53",
  "L 440.62 357.84",
  "L 429.76 349.31",
  "L 426.15 346.47",
  "L 423 344",
  "L 425.6 340.96",
  "L 430.49 335.24",
  "L 476.95 280.9",
  "L 479.4 278.04",
  "L 482 275",
  "L 479.94 271.57",
  "L 475.73 264.55",
  "L 467 250",
  "L 480.97 141.03",
  "L 481.49 136.97",
  "L 482 133",
].join(" ");

// Pulled in from the right edge so labels don't clip the frame
const METER_X = 880;
const METER_TOP_Y = 135;
const METER_BOT_Y = 595;

const STARS = [
  { x: 60, y: 50, r: 1.2, o: 0.7 },
  { x: 130, y: 80, r: 0.8, o: 0.5 },
  { x: 220, y: 30, r: 1.4, o: 0.85 },
  { x: 280, y: 110, r: 0.9, o: 0.6 },
  { x: 360, y: 60, r: 1.1, o: 0.7 },
  { x: 410, y: 40, r: 0.7, o: 0.4 },
  { x: 510, y: 90, r: 1.3, o: 0.8 },
  { x: 580, y: 50, r: 0.9, o: 0.55 },
  { x: 640, y: 30, r: 1.5, o: 0.95 },
  { x: 700, y: 75, r: 0.8, o: 0.5 },
  { x: 760, y: 100, r: 1.0, o: 0.6 },
  { x: 870, y: 50, r: 1.2, o: 0.75 },
  { x: 90, y: 120, r: 0.6, o: 0.35 },
  { x: 470, y: 25, r: 1.0, o: 0.6 },
];

/**
 * Pick a "nice" round step for the meter ticks based on the goal target.
 * For 150 → step=25, count=6  (0, 25, 50, 75, 100, 125, 150)
 * For 100 → step=20, count=5  (0, 20, 40, 60, 80, 100)
 * For 1000 → step=200, count=5
 */
const niceTickConfig = (target) => {
  if (!target || target <= 0) return { step: 1, count: 6 };
  const niceSteps = [
    1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000,
    10000, 20000, 25000, 50000, 100000,
  ];
  for (let i = 0; i < niceSteps.length; i += 1) {
    const step = niceSteps[i];
    const count = target / step;
    if (Number.isInteger(count) && count >= 4 && count <= 8) {
      return { step, count };
    }
  }
  // Fallback: arbitrary target — round step up to make 6 segments
  const rough = target / 6;
  const magnitude = 10 ** Math.floor(Math.log10(rough || 1));
  const step = Math.ceil(rough / magnitude) * magnitude;
  return { step, count: Math.ceil(target / step) };
};

const GoalMountain = ({
  goalTarget = 0,
  goalCurrent = 0,
  goalKpiTitle = "",
  goalPeriodLabel = "Monthly",
  mountainImageKey = "",
  darkMode = true,
  noScroll = false,
}) => {
  const company = UseCompany();
  const trailRef = useRef(null);
  const [pathLength, setPathLength] = useState(0);
  const mountainImageUrl = mountainImageKey ? GET_IMAGE(mountainImageKey) : null;
  const customImageMode = !!mountainImageUrl;

  useEffect(() => {
    if (trailRef.current) {
      setPathLength(trailRef.current.getTotalLength?.() || 0);
    }
  }, []);

  const pct = goalTarget > 0 ? Math.min(1, goalCurrent / goalTarget) : 0;
  const hasGoal = goalTarget > 0;

  const climberPoint = useMemo(() => {
    if (!pathLength || !trailRef.current) return { x: 413, y: 614 };
    const dist = pct * pathLength;
    const p = trailRef.current.getPointAtLength(dist);
    return { x: p.x, y: p.y };
  }, [pct, pathLength]);

  const meterValueY = METER_BOT_Y - pct * (METER_BOT_Y - METER_TOP_Y);

  const tickConfig = useMemo(() => niceTickConfig(goalTarget), [goalTarget]);
  const ticks = useMemo(() => {
    if (!hasGoal) return [];
    return Array.from({ length: tickConfig.count + 1 }).map((_, i) => {
      const value = i * tickConfig.step;
      const t = Math.min(1, value / goalTarget);
      return {
        y: METER_BOT_Y - t * (METER_BOT_Y - METER_TOP_Y),
        value,
      };
    });
  }, [tickConfig, goalTarget, hasGoal]);

  // -------- Color palette --------
  // Dark-mode sky-top color matches the HudFrame interior so there's no seam.
  const colors = darkMode
    ? {
        // Frame-matching root background — kills the horizontal seam at top.
        rootBg: "#142037",
        // Sky gradient: subtle, atmospheric night feel.
        sky1: "#142037",
        sky2: "#1A2C50",
        sky3: "#26467E",
        // Mountain art — deep dark-blue palette per the inspiration image
        mount2Far: "#1B1B1B",
        mount2Near: "#4F4E4E",
        mount3Far: "#2C548A", // lit face — deep navy
        mount3Near: "#14304F", // shadow face — much darker navy
        peakHighlight: "rgba(255,255,255,0.65)", // snowcap
        innerShadow: "rgba(0,0,0,0.28)",
        // UI
        text: "#F5F7FB",
        textMuted: "rgba(245,247,251,0.62)",
        textFaint: "rgba(245,247,251,0.38)",
        accent: "#5B8DEF",
        accentSoft: "rgba(91,141,239,0.22)",
        gold: "#F0B33D",
        // Meter
        meterTrack: "rgba(255,255,255,0.10)",
        meterTickLine: "rgba(255,255,255,0.30)",
        // Stars / moon
        moon: "#F4E9C8",
        starColor: "#FFF7D0",
        ridge: "#FFFFFF",
        // Metric card (matches KpiTile styling)
        cardBg: "rgba(255,255,255,0.06)",
        cardBorder: "rgba(255,255,255,0.10)",
      }
    : {
        rootBg: "#FAFBFD",
        // Daytime sky — near-white, just a hint of cool tone toward the horizon
        sky1: "#FFFFFF",
        sky2: "#F6F8FB",
        sky3: "#ECF1F6",
        // Mountain art — deep dark-blue palette per the inspiration image
        mount2Far: "#B0BFD0",
        mount2Near: "#8E9FB5",
        mount3Far: "#2C548A", // lit face — deep navy
        mount3Near: "#14304F", // shadow face — very dark navy
        peakHighlight: "rgba(255,255,255,0.85)", // bright snowcap
        innerShadow: "rgba(15,23,42,0.18)",
        text: "#0B1320",
        textMuted: "rgba(11,19,32,0.62)",
        textFaint: "rgba(11,19,32,0.38)",
        accent: "#3D7AC4",
        accentSoft: "rgba(61,122,196,0.16)",
        gold: "#E09A1F", // slightly deeper gold to read on light bg
        meterTrack: "rgba(15,23,42,0.14)", // visible on light bg
        meterTickLine: "rgba(15,23,42,0.40)",
        moon: "#FFF6D6",
        starColor: "transparent",
        ridge: "#FFFFFF",
        cardBg: "#FFFFFF",
        cardBorder: "rgba(15,23,42,0.10)",
      };

  const FONT = `'DM Sans', 'Inter', system-ui, sans-serif`;
  // Mono font for titles + meter ticks — gives the TV that broadcast/console feel.
  const MONO_FONT = `'Geist Mono', 'DM Sans', ui-monospace, SFMono-Regular, Menlo, monospace`;

  return (
    <Box
      sx={{
        width: "100%",
        height: noScroll ? "100%" : "auto",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: customImageMode ? "#0B1320" : colors.rootBg,
      }}
    >
      {(!customImageMode || hasGoal) && (
      <Box
        sx={{
          flexShrink: 0,
          position: "relative",
          zIndex: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          px: "1.4vw",
          pt: "1.4vh",
          pb: customImageMode ? "0.4vh" : "0.6vh",
        }}
      >
        <Box
          sx={{
            background: colors.cardBg,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: "16px",
            px: "2.2vw",
            py: "1.6vh",
            textAlign: "center",
            minWidth: "18vw",
            boxShadow: darkMode
              ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.18)"
              : "0 1px 0 rgba(255,255,255,0.6) inset, 0 6px 18px rgba(15,23,42,0.06)",
          }}
        >
          {hasGoal ? (
            <>
              {/* Hero score: 0 / 150 */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                  gap: "0.4vw",
                  fontFeatureSettings: '"tnum" 1, "lnum" 1',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 700,
                    fontSize: "4.4vh",
                    color: colors.text,
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                  }}
                >
                  {Math.round(goalCurrent)}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 500,
                    fontSize: "2.4vh",
                    color: colors.textMuted,
                    letterSpacing: "-0.015em",
                  }}
                >
                  / {Math.round(goalTarget)}
                </Box>
              </Box>
              {/* Metric name caption — Geist Mono for the broadcast feel */}
              <Box
                sx={{
                  fontFamily: MONO_FONT,
                  fontWeight: 700,
                  fontSize: "1.5vh",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: colors.gold,
                  mt: "0.8vh",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {goalKpiTitle || "—"}
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: "2.4vh",
                  color: colors.text,
                  letterSpacing: "-0.01em",
                }}
              >
                No goal set
              </Box>
              <Box
                sx={{
                  fontFamily: FONT,
                  fontWeight: 600,
                  fontSize: "1.1vh",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: colors.gold,
                  mt: "0.5vh",
                }}
              >
                Set one from the Company View
              </Box>
            </>
          )}
        </Box>
      </Box>
      )}

      {/* ────────── CUSTOM IMAGE or MOUNTAIN ART ────────── */}
      {customImageMode ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            position: "relative",
            zIndex: 1,
            background: darkMode ? "#0B1320" : colors.rootBg,
          }}
        >
          <Box
            component="img"
            src={mountainImageUrl}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center",
              display: "block",
            }}
          />
        </Box>
      ) : (
      <Box sx={{ flex: 1, position: "relative", minHeight: 0, px: "1.4vw", pb: "1.2vh", pt: "0.6vh" }}>
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMidYMax meet"
          style={{ width: "100%", height: "100%", display: "block" }}
        >
          <defs>
            <linearGradient id="gm-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.sky1} />
              <stop offset="55%" stopColor={colors.sky2} />
              <stop offset="100%" stopColor={colors.sky3} />
            </linearGradient>
            <linearGradient id="gm-meter-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#A8C0F0" />
              <stop offset="100%" stopColor={colors.accent} />
            </linearGradient>
            <linearGradient id="gm-mount3near-shading" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.mount3Near} stopOpacity="1" />
              <stop offset="100%" stopColor={colors.mount3Near} stopOpacity="0.85" />
            </linearGradient>
            <filter id="gm-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="gm-moon-glow">
              <stop offset="0%" stopColor={colors.moon} stopOpacity="0.5" />
              <stop offset="100%" stopColor={colors.moon} stopOpacity="0" />
            </radialGradient>
            <radialGradient id="gm-sun-glow">
              <stop offset="0%" stopColor="#FFE9A8" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#FFE9A8" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Sky */}
          <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="url(#gm-sky)" />

          {/* Stars (dark mode) */}
          {darkMode &&
            STARS.map((s) => (
              <circle
                key={`star-${s.x}-${s.y}`}
                cx={s.x}
                cy={s.y}
                r={s.r}
                fill={colors.starColor}
                opacity={s.o}
              />
            ))}
          {darkMode && (
            <>
              <circle cx="180" cy="65" r="1.3" fill={colors.starColor}>
                <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" />
              </circle>
              <circle cx="540" cy="40" r="1.5" fill={colors.starColor}>
                <animate attributeName="opacity" values="0.4;1;0.4" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle cx="800" cy="70" r="1.2" fill={colors.starColor}>
                <animate attributeName="opacity" values="0.5;1;0.5" dur="3.6s" repeatCount="indefinite" />
              </circle>
            </>
          )}

          {/* Moon (dark) — top-right */}
          {darkMode && (
            <g>
              <circle cx="820" cy="95" r="48" fill="url(#gm-moon-glow)" />
              <circle cx="820" cy="95" r="22" fill={colors.moon} opacity="0.95" />
              <circle cx="815" cy="92" r="3" fill="#D9CC9C" opacity="0.4" />
              <circle cx="827" cy="100" r="2" fill="#D9CC9C" opacity="0.35" />
            </g>
          )}

          {/* Sun glow + clouds (light mode) */}
          {!darkMode && (
            <>
              {/* Soft warm sun glow in top-right */}
              <circle cx="820" cy="100" r="80" fill="url(#gm-sun-glow)" />
              {/* Soft clouds — distant, subtle */}
              <g opacity="0.55">
                <ellipse cx="160" cy="120" rx="50" ry="9" fill="#FFFFFF" />
                <ellipse cx="180" cy="115" rx="32" ry="7" fill="#FFFFFF" />
                <ellipse cx="630" cy="160" rx="60" ry="10" fill="#FFFFFF" />
                <ellipse cx="650" cy="155" rx="38" ry="8" fill="#FFFFFF" />
                <ellipse cx="380" cy="90" rx="44" ry="7" fill="#FFFFFF" opacity="0.8" />
              </g>
            </>
          )}

          {/* ───── Mountain art ───── */}
          {/* mount2 — left side wing */}
          <g>
            <polygon
              fill={colors.mount2Far}
              points="483,562 418,376 381.68,344.02 360,316 345,316 286,241 239,321 199,395 179.01,505.28 207,554 211.98,614.6 502.5,614.6"
            />
            <polygon
              fill={colors.mount2Near}
              points="0,615 0,523 69,438 107,412 126,368 207,321 248,265 286,241 278,295 235,374 248,396 223,438 262,547 262.5,614.5"
            />
          </g>
          {/* mount1 — right side wing */}
          <g>
            <polygon
              fill={colors.mount2Far}
              points="770.65,615 965,615 965,476 941,452 901,443 892,404 852,374 822,314 785,282 755,342 755,436 758,540"
            />
            <polygon
              fill={colors.mount2Near}
              points="896.17,615 907,602 838,590 860,548 812,516 819,457 788,442 822,404 792,366 779,309 785,282 764,314 721,325 694,363 687,444 635,533 613,540 575.5,615"
            />
          </g>
          {/* mount3 — central front mountain (the climbed one) */}
          <g>
            <polygon
              fill={colors.mount3Far}
              points="42.5,614.5 111.5,523.5 204.5,494.5 228.5,440.5 346.5,392.5 375.5,348.5 415.5,319.5 482.5,133.5 487.5,133.5 520.5,185.5 564.5,222.5 582.5,259.5 703.5,346.5 712.5,441.5 758.5,467.5 833.5,572.5 885.5,614.5"
            />
            <polygon
              fill="url(#gm-mount3near-shading)"
              points="457.5,552.5 441.5,513.5 482.5,475.5 451.5,463.5 419.5,431.5 451.5,366.5 423.5,344.5 480.5,275.5 467.5,250.5 481.39,136.57 415.5,319.5 375.5,348.5 346.5,392.5 228.5,440.5 204.5,494.5 111.5,523.5 42.5,614.5 413.45,614.5"
            />
            {/* Subtle inner shadow on the front face for depth */}
            <polygon
              fill={colors.innerShadow}
              opacity="0.5"
              points="481.39,136.57 415.5,319.5 375.5,348.5 346.5,392.5 228.5,440.5 204.5,494.5 200,520 250,490 320,440 380,380 430,340 470,250"
            />
          </g>

          {/* (No snowcap — the rotating trophy at the peak is the visual marker.) */}

          {/* ───── ROTATING 3D TROPHY at the peak ─────
              Same Trophy3D component used at the TV's KOTM peak. The
              foreignObject is positioned so the trophy's base lands AT the
              peak (SVG y=133), not below it — the trophy sits on the peak
              instead of overlapping the ridge. */}
          <foreignObject x="433" y="37" width="115" height="115">
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              style={{ width: "100%", height: "100%" }}
            >
              <Trophy3D size="100%" />
            </div>
          </foreignObject>

          {/* ───── Climbing trail (verbatim from KOTM) ───── */}
          <path
            ref={trailRef}
            d={TRAIL_PATH}
            fill="none"
            stroke={colors.ridge}
            strokeWidth="3"
            strokeDasharray="8 8"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={darkMode ? 0.95 : 1}
          />

          {/* ───── Right-side meter ───── */}
          <rect
            x={METER_X - 8}
            y={METER_TOP_Y - 6}
            width="16"
            height={METER_BOT_Y - METER_TOP_Y + 12}
            rx="8"
            fill={colors.meterTrack}
            stroke={darkMode ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.12)"}
            strokeWidth="1"
          />
          {/* Meter fill — animates smoothly when value changes */}
          <rect
            x={METER_X - 8}
            y={meterValueY}
            width="16"
            height={METER_BOT_Y - meterValueY + 6}
            rx="8"
            fill="url(#gm-meter-fill)"
            style={{ transition: "y 800ms cubic-bezier(0.2, 0.8, 0.2, 1), height 800ms cubic-bezier(0.2, 0.8, 0.2, 1)" }}
          />
          {/* Baseline indicator at meter 0 — always visible so it doesn't look broken at 0% */}
          <circle
            cx={METER_X}
            cy={METER_BOT_Y}
            r="4.5"
            fill={colors.accent}
            stroke="#FFFFFF"
            strokeWidth="1.5"
            opacity="0.85"
          />
          {/* Ticks + labels (round numbers) */}
          {ticks.map((tk, i) => (
            <g key={`tick-${tk.y}`}>
              <line
                x1={METER_X + 12}
                y1={tk.y}
                x2={METER_X + 22}
                y2={tk.y}
                stroke={colors.meterTickLine}
                strokeWidth="1.5"
              />
              <text
                x={METER_X + 28}
                y={tk.y + 4}
                fontFamily={MONO_FONT}
                fontSize="13"
                fontWeight={i === ticks.length - 1 ? 700 : 500}
                fill={i === ticks.length - 1 ? colors.gold : colors.textMuted}
                style={{ letterSpacing: "-0.02em" }}
              >
                {tk.value}
              </text>
            </g>
          ))}

          {/* ───── Connector + climber ───── */}
          {hasGoal && (
            <g>
              {/* Connector — subtle dashed line, fading toward the climber side */}
              <line
                x1={climberPoint.x + 14}
                y1={climberPoint.y}
                x2={METER_X - 10}
                y2={meterValueY}
                stroke={colors.accent}
                strokeWidth="1.6"
                strokeDasharray="5 5"
                strokeLinecap="round"
                opacity="0.55"
                style={{ transition: "x1 800ms cubic-bezier(0.2,0.8,0.2,1), y1 800ms cubic-bezier(0.2,0.8,0.2,1), y2 800ms cubic-bezier(0.2,0.8,0.2,1)" }}
              />
              {/* Strong endpoint dot on the meter at the climber's level */}
              <circle
                cx={METER_X}
                cy={meterValueY}
                r="7"
                fill={colors.accent}
                stroke="#FFFFFF"
                strokeWidth="2.5"
                filter="url(#gm-glow)"
                style={{ transition: "cy 800ms cubic-bezier(0.2,0.8,0.2,1)" }}
              />
            </g>
          )}

          {/* Climber (the company) — animates smoothly along the trail.
              The avatar shows the company's actual logo (resolved via
              GET_IMAGE so relative storage keys work). Fallback: a gold-tinted
              circle with the first letter of the company name in mono. */}
          <g
            style={{
              transform: `translate(${climberPoint.x - 22}px, ${climberPoint.y - 50}px)`,
              transition: "transform 800ms cubic-bezier(0.2, 0.8, 0.2, 1)",
            }}
          >
            {/* Soft glow disc behind avatar */}
            <circle cx="22" cy="22" r="30" fill={colors.accentSoft} opacity="0.6" />
            {/* Outer accent ring */}
            <circle cx="22" cy="22" r="23" fill={colors.gold} opacity="0.9" />
            {/* White inner ring */}
            <circle cx="22" cy="22" r="20" fill="#FFFFFF" />
            {/* Inner clipped logo (or initial fallback) */}
            <defs>
              <clipPath id="gm-climber-clip">
                <circle cx="22" cy="22" r="19" />
              </clipPath>
            </defs>
            {company?.logo ? (
              <image
                x="3"
                y="3"
                width="38"
                height="38"
                href={GET_IMAGE(company.logo)}
                clipPath="url(#gm-climber-clip)"
                preserveAspectRatio="xMidYMid slice"
              />
            ) : (
              <g clipPath="url(#gm-climber-clip)">
                <rect x="3" y="3" width="38" height="38" fill={colors.gold} opacity="0.18" />
                <text
                  x="22"
                  y="29"
                  textAnchor="middle"
                  fontFamily={MONO_FONT}
                  fontSize="18"
                  fontWeight="700"
                  fill={colors.accent}
                  style={{ letterSpacing: "-0.02em" }}
                >
                  {(company?.businessName || "C")[0]?.toUpperCase()}
                </text>
              </g>
            )}
            {/* Pin pointing down to the trail */}
            <path d="M 16 44 L 22 52 L 28 44 Z" fill={colors.gold} />
          </g>

          {/* "YOU ARE HERE" pill — positioned to the SIDE of the climber instead of
              above so it doesn't overlap the avatar. Side depends on which side
              of the SVG the climber is on. */}
          {hasGoal && (
            <g
              style={{
                transform: `translate(${climberPoint.x + (climberPoint.x > VIEW_W / 2 ? -150 : 32)}px, ${climberPoint.y - 12}px)`,
                transition: "transform 800ms cubic-bezier(0.2, 0.8, 0.2, 1)",
              }}
            >
              <rect
                x="0"
                y="0"
                width="118"
                height="22"
                rx="11"
                fill={darkMode ? "rgba(91,141,239,0.95)" : "#FFFFFF"}
                stroke={colors.accent}
                strokeWidth="1.4"
              />
              <text
                x="59"
                y="15"
                textAnchor="middle"
                fontFamily={FONT}
                fontSize="11"
                fontWeight="700"
                fill={darkMode ? "#FFFFFF" : colors.accent}
                style={{ letterSpacing: "0.1em" }}
              >
                YOU ARE HERE
              </text>
              {/* Pointer triangle pointing toward the climber */}
              <path
                d={
                  climberPoint.x > VIEW_W / 2
                    ? "M 118 11 L 124 11 L 121 14 Z"
                    : "M 0 11 L -6 11 L -3 14 Z"
                }
                fill={darkMode ? "rgba(91,141,239,0.95)" : "#FFFFFF"}
                stroke={colors.accent}
                strokeWidth="1.4"
              />
            </g>
          )}
        </svg>
      </Box>
      )}
    </Box>
  );
};

export default GoalMountain;
